import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, QrCode, Package, Printer, ExternalLink, Camera, Lock, Zap } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  getIngredients, getRecipes, getSubscription,
  getIngredientBatches, createIngredientBatch, deleteIngredientBatch,
  getTraceabilityRecords, createTraceabilityRecord,
  uploadBatchPhoto, getBatchPhoto,
} from '../api/client'

const today = new Date().toISOString().slice(0, 10)

export default function TraceabilityPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [tab, setTab] = useState<'batches' | 'records'>('batches')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: sub } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => getSubscription().then(r => r.data),
  })
  const hasPhotoAddon = !!sub?.invoice_photos_addon

  const { data: ingredients = [] } = useQuery({
    queryKey: ['ingredients'],
    queryFn: () => getIngredients().then(r => r.data),
    staleTime: 60000,
  })
  const { data: recipes = [] } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => getRecipes().then(r => r.data),
    staleTime: 60000,
  })
  const { data: batches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ['ingredient-batches'],
    queryFn: () => getIngredientBatches().then(r => r.data),
  })
  const { data: records = [], isLoading: recordsLoading } = useQuery({
    queryKey: ['traceability-records'],
    queryFn: () => getTraceabilityRecords().then(r => r.data),
  })

  const [batchForm, setBatchForm] = useState({ ingredient_id: '', supplier_name: '', batch_number: '', invoice_number: '', received_at: today, note: '' })
  const [photoFile, setPhotoFile] = useState<File | null>(null)

  const createBatchMut = useMutation({
    mutationFn: async () => {
      const res = await createIngredientBatch({
        ingredient_id: Number(batchForm.ingredient_id),
        supplier_name: batchForm.supplier_name,
        batch_number: batchForm.batch_number || null,
        invoice_number: batchForm.invoice_number || null,
        received_at: batchForm.received_at,
        note: batchForm.note || null,
      })
      if (photoFile && hasPhotoAddon) {
        await uploadBatchPhoto(res.data.id, photoFile)
      }
      return res
    },
    onSuccess: () => {
      toast.success('Alapanyag-tétel rögzítve')
      setBatchForm({ ingredient_id: '', supplier_name: '', batch_number: '', invoice_number: '', received_at: today, note: '' })
      setPhotoFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      qc.invalidateQueries({ queryKey: ['ingredient-batches'] })
    },
    onError: () => toast.error('Nem sikerült menteni'),
  })
  const deleteBatchMut = useMutation({
    mutationFn: (id: number) => deleteIngredientBatch(id),
    onSuccess: () => {
      toast.success('Törölve')
      qc.invalidateQueries({ queryKey: ['ingredient-batches'] })
    },
  })

  const uploadRetroMut = useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) => uploadBatchPhoto(id, file),
    onSuccess: () => {
      toast.success('Számla-fotó feltöltve')
      qc.invalidateQueries({ queryKey: ['ingredient-batches'] })
    },
    onError: () => toast.error('Nem sikerült feltölteni'),
  })

  async function viewPhoto(batchId: number) {
    try {
      const res = await getBatchPhoto(batchId)
      const url = window.URL.createObjectURL(res.data)
      window.open(url, '_blank')
    } catch {
      toast.error('Nem sikerült betölteni a fotót')
    }
  }

  const [recordForm, setRecordForm] = useState({ recipe_id: '', production_date: today })
  const createRecordMut = useMutation({
    mutationFn: () => createTraceabilityRecord({ recipe_id: Number(recordForm.recipe_id), production_date: recordForm.production_date }),
    onSuccess: () => {
      toast.success('Nyomonkövetési QR elkészült')
      qc.invalidateQueries({ queryKey: ['traceability-records'] })
    },
    onError: () => toast.error('Nem sikerült létrehozni'),
  })

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-dark-text">Nyomonkövetés</h1>
        <p className="text-sm text-gray-500 mt-1">
          Rögzítsd, melyik beszállítótól, milyen tétel/számla-számmal érkezett egy alapanyag, majd generálj hozzá QR-kódot egy adott gyártáshoz — a kód beolvasva megmutatja, mikből készült a termék.
        </p>
      </div>

      {!hasPhotoAddon && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-6 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <Camera className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-900">Számla-fotó tárolás</p>
              <p className="text-xs text-gray-500 mt-0.5">Fotózd le a beszállítói számlát minden tételhez — 990 Ft + ÁFA / hó, bővítményként.</p>
            </div>
          </div>
          <button className="shrink-0 flex items-center gap-1.5 text-xs font-semibold bg-amber-400 hover:bg-amber-500 text-white px-4 py-2 rounded-lg transition-colors"
            onClick={() => navigate('/elofizetes')}>
            <Zap size={14} /> Bekapcsolás
          </button>
        </div>
      )}

      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab('batches')} className={tab === 'batches' ? 'btn-primary' : 'btn-secondary'}>
          <Package size={16} /> Alapanyag-tételek
        </button>
        <button onClick={() => setTab('records')} className={tab === 'records' ? 'btn-primary' : 'btn-secondary'}>
          <QrCode size={16} /> Nyomonkövetési QR-kódok
        </button>
      </div>

      {tab === 'batches' && (
        <div className="flex flex-col gap-6">
          <div className="card">
            <p className="section-title">Új alapanyag-tétel rögzítése</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end">
              <div>
                <label className="label">Alapanyag</label>
                <select className="input" value={batchForm.ingredient_id} onChange={e => setBatchForm(f => ({ ...f, ingredient_id: e.target.value }))}>
                  <option value="">— Válassz —</option>
                  {(ingredients as any[]).map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Beszállító</label>
                <input className="input" value={batchForm.supplier_name} onChange={e => setBatchForm(f => ({ ...f, supplier_name: e.target.value }))} placeholder="pl. Metro Kft." />
              </div>
              <div>
                <label className="label">Érkezés dátuma</label>
                <input type="date" className="input" value={batchForm.received_at} onChange={e => setBatchForm(f => ({ ...f, received_at: e.target.value }))} />
              </div>
              <div>
                <label className="label">Tétel / LOT szám</label>
                <input className="input" value={batchForm.batch_number} onChange={e => setBatchForm(f => ({ ...f, batch_number: e.target.value }))} placeholder="a csomagoláson szereplő azonosító" />
              </div>
              <div>
                <label className="label">Számlaszám</label>
                <input className="input" value={batchForm.invoice_number} onChange={e => setBatchForm(f => ({ ...f, invoice_number: e.target.value }))} placeholder="pl. SZ-2026-1234" />
              </div>
              <button
                className="btn-primary"
                disabled={!batchForm.ingredient_id || !batchForm.supplier_name || createBatchMut.isPending}
                onClick={() => createBatchMut.mutate()}
              >
                <Plus size={16} /> Rögzítés
              </button>
            </div>
            <div className="mt-3">
              {hasPhotoAddon ? (
                <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={e => setPhotoFile(e.target.files?.[0] || null)}
                  />
                  <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50">
                    <Camera size={14} /> {photoFile ? photoFile.name : 'Számla lefotózása (opcionális)'}
                  </span>
                </label>
              ) : (
                <span className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Lock size={13} /> Számla-fotó csatolása a bővítménnyel érhető el
                </span>
              )}
            </div>
          </div>

          <div className="card">
            <p className="section-title">Rögzített tételek</p>
            {batchesLoading ? (
              <p className="text-sm text-gray-400">Betöltés...</p>
            ) : (batches as any[]).length === 0 ? (
              <p className="text-sm text-gray-400">Még nincs rögzített alapanyag-tétel.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
                      <th className="py-2 pr-4">Alapanyag</th>
                      <th className="py-2 pr-4">Beszállító</th>
                      <th className="py-2 pr-4">Tétel/LOT szám</th>
                      <th className="py-2 pr-4">Számlaszám</th>
                      <th className="py-2 pr-4">Érkezett</th>
                      <th className="py-2 pr-4">Számla-fotó</th>
                      <th className="py-2 pr-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(batches as any[]).map(b => (
                      <tr key={b.id} className="border-b border-gray-50">
                        <td className="py-2 pr-4 font-medium text-dark-text">{b.ingredient_name}</td>
                        <td className="py-2 pr-4">{b.supplier_name}</td>
                        <td className="py-2 pr-4 text-gray-500">{b.batch_number || '—'}</td>
                        <td className="py-2 pr-4 text-gray-500">{b.invoice_number || '—'}</td>
                        <td className="py-2 pr-4 text-gray-500">{b.received_at}</td>
                        <td className="py-2 pr-4">
                          {b.has_photo ? (
                            <button className="btn-ghost" onClick={() => viewPhoto(b.id)}>
                              <Camera size={14} /> Megnézés
                            </button>
                          ) : hasPhotoAddon ? (
                            <>
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                id={`retro-photo-${b.id}`}
                                onChange={e => {
                                  const file = e.target.files?.[0]
                                  if (file) uploadRetroMut.mutate({ id: b.id, file })
                                }}
                              />
                              <label htmlFor={`retro-photo-${b.id}`} className="btn-ghost cursor-pointer">
                                <Camera size={14} /> Feltöltés
                              </label>
                            </>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-right">
                          <button className="btn-ghost" onClick={() => deleteBatchMut.mutate(b.id)}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'records' && (
        <div className="flex flex-col gap-6">
          <div className="card">
            <p className="section-title">Nyomonkövetési QR generálása egy gyártáshoz</p>
            <p className="text-xs text-gray-400 mb-4">
              A recept minden alapanyagához a legutóbb rögzített tételt kapcsolja hozzá, és egy publikus, QR-kóddal beolvasható oldalt generál.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div>
                <label className="label">Recept</label>
                <select className="input" value={recordForm.recipe_id} onChange={e => setRecordForm(f => ({ ...f, recipe_id: e.target.value }))}>
                  <option value="">— Válassz —</option>
                  {(recipes as any[]).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Gyártás dátuma</label>
                <input type="date" className="input" value={recordForm.production_date} onChange={e => setRecordForm(f => ({ ...f, production_date: e.target.value }))} />
              </div>
              <button
                className="btn-primary"
                disabled={!recordForm.recipe_id || createRecordMut.isPending}
                onClick={() => createRecordMut.mutate()}
              >
                <QrCode size={16} /> QR generálása
              </button>
            </div>
          </div>

          <div className="card">
            <p className="section-title">Korábbi nyomonkövetési QR-kódok</p>
            {recordsLoading ? (
              <p className="text-sm text-gray-400">Betöltés...</p>
            ) : (records as any[]).length === 0 ? (
              <p className="text-sm text-gray-400">Még nincs generált nyomonkövetési QR-kód.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(records as any[]).map(rec => (
                  <div key={rec.id} className="card-sm flex flex-col items-center text-center gap-2">
                    <img src={rec.qr_data_uri} alt="QR kód" className="w-32 h-32" />
                    <p className="font-semibold text-sm text-dark-text">{rec.recipe_name}</p>
                    <p className="text-xs text-gray-400">Gyártás: {rec.production_date}{rec.best_before_date && <> · Fogyasztható: {rec.best_before_date}</>}</p>
                    <div className="flex gap-2 mt-1">
                      <a href={rec.qr_data_uri} download={`nyomonkovetes-${rec.recipe_name}-${rec.production_date}.png`} className="btn-ghost">
                        <Printer size={14} /> Letöltés
                      </a>
                      <a href={rec.public_url} target="_blank" rel="noopener noreferrer" className="btn-ghost">
                        <ExternalLink size={14} /> Megnyitás
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
